import { z } from "zod";

/**
 * CDL v1 — el grafo de hechos (ontología B22).
 *
 * Un caso deja de ser "lista de artefactos + solución" y pasa a ser un grafo:
 * HECHOS tipados unidos por aristas lógicas, montados sobre artefactos vía
 * CARRIERS. El jugador nunca ve un hecho: ve artefactos; los hechos son la
 * columna vertebral que valida el authoring hoy y que el pipeline de F2
 * genera, simula y verifica.
 */

export const FactType = z.enum([
  "coartada",
  "relacion",
  "movil",
  "medio",
  "oportunidad",
  "identidad",
  "circunstancia",
]);

export const FactV1 = z.object({
  id: z.string().regex(/^F\d+$/),
  type: FactType,
  /** Enunciado interno de authoring/pipeline — JAMÁS se muestra al jugador (G-23) */
  statement: z.string().min(1),
  /** CharacterIds implicados */
  about: z.array(z.string().regex(/^C\d+$/)).default([]),
  /**
   * Hechos base viven en carriers; los derivados existen solo cuando el
   * jugador combina sus fuentes (el "ahá" es siempre un hecho derivado).
   */
  derivedFrom: z.array(z.string().regex(/^F\d+$/)).default([]),
});

export const FactEdgeV1 = z.object({
  from: z.string().regex(/^F\d+$/),
  to: z.string().regex(/^F\d+$/),
  rel: z.enum(["prueba", "contradice", "sugiere", "leads_to"]),
});

/** Un hecho llega al jugador montado en un artefacto concreto */
export const CarrierRefV1 = z.object({
  factId: z.string().regex(/^F\d+$/),
  artifactId: z.string().regex(/^A\d+$/),
  /** explicito: se lee directo · latente: exige cruce, zoom o atención (el ahá) */
  presence: z.enum(["explicito", "latente"]),
});

/**
 * Pista falsa declarada (jugabilidad §3): un hecho señuelo con su refutación.
 * El validador exige que TODA pista falsa sea refutable con hechos del caso —
 * un herring irrefutable es un caso injusto.
 */
export const RedHerringV1 = z.object({
  id: z.string().min(1),
  /** El hecho que sugiere la conclusión equivocada */
  factId: z.string().regex(/^F\d+$/),
  /** Los hechos que lo desactivan */
  refutedBy: z.array(z.string().regex(/^F\d+$/)).min(1),
});

export type FactV1Data = z.infer<typeof FactV1>;
export type FactEdgeV1Data = z.infer<typeof FactEdgeV1>;
export type CarrierRefV1Data = z.infer<typeof CarrierRefV1>;
export type RedHerringV1Data = z.infer<typeof RedHerringV1>;
